import { router } from '@inertiajs/react';
import { Link } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

import { FileUploader } from '@/components/file-uploader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import AppLayout from '@/layouts/app-layout';


interface NewShareProps {
  share: {
    title: string;
    description: string;
    expires_at: string | null;
  };
  errors?: string[];
}

export default function NewShare({ share, errors }: NewShareProps) {
  const [formData, setFormData] = useState({
    title: share.title || '',
    description: share.description || '',
    expires_at: share.expires_at ?? ''
  });
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    router.post('/shares', {
      share: formData,
      file_ids: fileIds
    }, {
      onFinish: () => setIsSubmitting(false)
    });
  };

  const handleFilesUploaded = (newFileIds: string[]) => {
    setFileIds(prev => [...prev, ...newFileIds]);
  };

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <Link href="/shares" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shares
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create New Share</CardTitle>
            <CardDescription>
              Upload files and create a shareable link
            </CardDescription>
          </CardHeader>
          <CardContent>
            {errors && errors.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <h3 className="text-red-800 dark:text-red-200 font-semibold mb-2">
                  Please fix the following errors:
                </h3>
                <ul className="list-disc list-inside text-red-700 dark:text-red-300">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter a title for your share"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Add an optional description"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600"
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="expires_at">Expiration Date (Optional)</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank for no expiration
                </p>
              </div>

              <div>
                <Label>Files</Label>
                <div className="mt-2">
                  <FileUploader onFilesUploaded={handleFilesUploaded} />
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <Link href="/shares">
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Link>
                <Button 
                  type="submit" 
                  disabled={isSubmitting || fileIds.length === 0}
                >
                  {isSubmitting ? 'Creating...' : 'Create Share'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}